import {isNil} from 'ramda';

import {CompositionFont} from '@dms/api/sales';

import {resolveFontFace} from './resolveFontFace';
import {
  BOLD_FONT_WEIGHT,
  FontFacePatch,
  REGULAR_FONT_WEIGHT,
  toFontFacePatch,
} from './toFontFacePatch';

export type FontFaceToggle = {
  isActive: boolean;
  // Layer patch that flips the toggle; null when the font has no face for the flipped state.
  patch: FontFacePatch | null;
};

/**
 * Bold and Italic toggle state of a text layer. An uploaded face flips to the sibling face of its
 * family. The built-in font has regular and bold faces and no italic face. A layer whose uploaded
 * face is no longer listed renders with the built-in font, so its toggles switch to the built-in
 * font.
 */
export const getFontFaceToggles = (layer: FontFacePatch, fonts: CompositionFont[]) => {
  const face = fonts.find((font) => font.id === layer.fontId);

  if (isNil(face)) {
    const isBold = layer.fontWeight === BOLD_FONT_WEIGHT;
    const bold: FontFaceToggle = {
      isActive: isBold,
      patch: {fontId: null, fontWeight: isBold ? REGULAR_FONT_WEIGHT : BOLD_FONT_WEIGHT},
    };
    const italic: FontFaceToggle = {isActive: false, patch: null};
    return {face: null, bold, italic};
  }

  const toggles = {isBold: face.weight === BOLD_FONT_WEIGHT, isItalic: face.style === 'italic'};
  const toToggle = (isActive: boolean, sibling: CompositionFont | null): FontFaceToggle => ({
    isActive,
    patch: isNil(sibling) ? null : toFontFacePatch(sibling),
  });

  return {
    face,
    bold: toToggle(
      toggles.isBold,
      resolveFontFace(fonts, face.family, {...toggles, isBold: !toggles.isBold})
    ),
    italic: toToggle(
      toggles.isItalic,
      resolveFontFace(fonts, face.family, {...toggles, isItalic: !toggles.isItalic})
    ),
  };
};
